import { FC, useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Spacings, breakpoints } from '@styles/index';
import Paper from '@base/Paper/Paper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { readCookie } from '@utils/cookies';
import Avatar from '@base/Avatar/Avatar';
import Dropdown from '@comp/Dropdown/Dropdown';
import Button from '@base/Button/Button';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import Login from '@cont/Login/Login';
import Typography from 'aquilla/core/atoms/Typography';
import useStates from '@api/hooks/useStates';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useWindowSize from '@hooks/useWindowSize';
import { getIsLoggedIn, getMe } from '@api/selectors/auth';
import useMounted from '@hooks/useMounted';
import { updateUserDetails } from '@api/fetchers/auth';
import usePaymentMethods from '@hooks/usePaymentMethods';

const Container = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  padding: ${Spacings.SPACING_7B} ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_3B};
  display: flex;
  justify-content: space-between;
  box-shadow: ${boxShadowGenerator()};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    padding: ${Spacings.SPACING_6B};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    padding: ${Spacings.SPACING_4B};
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const Info = styled(Typography)`
  margin-top: ${Spacings.SPACING_2B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const DetailsContainer = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_3B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    padding: ${Spacings.SPACING_6B};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    padding: ${Spacings.SPACING_4B};
    margin-bottom: 0px;
    position: relative;
    padding-bottom: ${Spacings.SPACING_14B};

    .term-and-condition {
      bottom: ${Spacings.SPACING_2B};
    }
  }
`;

const StateInputContainer = styled(Paper)`
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_3B};
`;

const DropdownWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(10, ${Spacings.SPACING_8B});
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_8B};
`;

const SelectState = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_2B};
`;

const Name = styled(Typography)`
  @media only screen and (max-width: ${breakpoints.tablet}) {
    font-size: 20px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
  }
`;

const UserAvatar = styled(Avatar)`
  cursor: default;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`;

const PageLoader = styled(Container)`
  height: 106px;
  align-items: center;
  justify-content: center;
`;

const LoginComp = Login as any;

const getUserInfo = (phoneCode, phone, email, state) => {
  const userInfo = [];
  const mobileNumberText =
    phone && phoneCode ? `+${phoneCode} ${phone}` : phone;
  if (mobileNumberText) userInfo.push(mobileNumberText);
  if (email) userInfo.push(email);
  if (state) userInfo.push(state);

  return (
    <Info variant="p2">
      {userInfo.map((item, index) => (
        <>
          <span key={item}>{item}</span>
          {index !== userInfo.length - 1 && (
            <span style={{ marginLeft: 8, marginRight: 8 }}> • </span>
          )}
        </>
      ))}
    </Info>
  );
};

const UserInfo: FC<{}> = () => {
  const { myInfo, refetchMyInfo } = useMyInfo();
  const { isTablet, isMobile } = useWindowSize();
  const isLoggedIn = getIsLoggedIn(myInfo);
  const isAccessToken = !!readCookie('accessToken');
  const isMounted = useMounted();

  const {
    name,
    state,
    email,
    phone,
    country: { phone_code = '' } = {},
    thumbnail
  } = myInfo || {};

  const {
    state: { mwebCheckoutV2 }
  } = usePaymentMethods();

  const [selectedState, setSelectedState] = useState('default');
  const { states } = useStates();
  const stateOptions = useMemo(() => {
    const stateOptionsArr = states.map((stateData) => ({
      label: stateData.name,
      value: stateData.code
    }));
    return [
      { label: 'Select - State of residence', value: 'default' },
      ...stateOptionsArr
    ];
  }, [states]);

  const afterStateSelection = useCallback(async () => {
    const updateUserResp: any = await updateUserDetails({
      state_code: selectedState
    });

    if (updateUserResp?.success) {
      refetchMyInfo();
    }
  }, [selectedState, refetchMyInfo]);

  if (isLoggedIn) {
    if (state || (!state && states.length < 1)) {
      if (mwebCheckoutV2) return null;
      return (
        <Container>
          <div>
            <Name variant="h3">{name}</Name>
            {getUserInfo(phone_code, phone, email, state)}
          </div>
          {!isMobile && !isTablet && <UserAvatar src={thumbnail} size="56px" />}
        </Container>
      );
    }
    return (
      <StateInputContainer>
        <SelectState variant="h3">Select state</SelectState>
        <Typography variant="p2">
          To continue, please select your Select - State of residence
        </Typography>
        <DropdownWrapper>
          <Dropdown
            // @ts-ignore
            gridColumn="span 10"
            mwGridColumn="span 5"
            options={stateOptions}
            onOptionClick={(e) => setSelectedState(e.value)}
            selectedOption={stateOptions.find(
              (option) => option.value === selectedState
            )}
          />
        </DropdownWrapper>
        <Button
          // @ts-ignore
          label="Continue"
          variant={selectedState ? 'filled' : 'disabled'}
          onClick={afterStateSelection}
        />
      </StateInputContainer>
    );
  }

  if (mwebCheckoutV2) return null;

  if (!isLoggedIn && !isAccessToken && isMounted) {
    return (
      <DetailsContainer>
        <LoginComp source="page" />
      </DetailsContainer>
    );
  }

  return (
    <PageLoader>
      <CircularLoader />
    </PageLoader>
  );
};

export default UserInfo;
